<script setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {formatNumber} from "../../utils/numberFormat.js";
const { locale, t } = useI18n();

const props = defineProps({
    fetchData: {
        type: Function,
        required: true,
    },
    totalItems: {
        type: Object,
        required: true,
    },
    currentPage: {
        type: Number,
        default: 1,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["page-change"]);
const current = ref(props.currentPage);

watch(
    () => props.currentPage,
    (val) => (current.value = val)
);

const totalPages = computed(() =>
    props.totalItems.totalPages
);

const isPrevDisabled = computed(() => current.value <= 1);
const isNextDisabled = computed(() => current.value >= totalPages.value);

function onFirst() {
    if (current.value === 1) return;
    current.value = 1;
    triggerFetch();
}

function onPrev() {
    if (isPrevDisabled.value) return;
    current.value--;
    triggerFetch();
}

function onNext() {
    if (isNextDisabled.value) return;
    current.value++;
    triggerFetch();
}

function onLast() {
    if (isNextDisabled.value) return;
    current.value = totalPages.value;
    triggerFetch();
}

const selectedPage = ref(props.currentPage);

watch(
    () => props.currentPage,
    (newVal) => {
        selectedPage.value = newVal;
    }
);

const totalPagesArray = computed(() => {
    return Array.from({ length: totalPages.value}, (_, i) => i + 1);
});
const search = ref("");

const filteredItems = computed(() => {
    const list = totalPagesArray.value;

    const filtered = !search.value
        ? list
        : list.filter(item =>
            item.toString().includes(search.value)
        );

    return filtered.slice(0, 10).map(item => ({
        title: formatNumber(item, locale.value, 0),
        value: item
    }));
});

function emitPageChange() {
    emit("page-change", selectedPage.value);

}

async function triggerFetch() {
    emit("page-change", current.value);
}

</script>

<template>
    <v-row class="pagination-container"  v-if="!loading && props.totalItems.totalItems">
            <v-col cols="12" sm="5" class="pagination-info d-flex align-center gap-2">
                <v-col cols="6" sm="12" class="pagination-info d-flex justify-start">
                    <span>
                        {{ formatNumber((currentPage - 1) * props.totalItems.limit + 1, locale, 0 )   }} -
                        {{ formatNumber(Math.min(current * props.totalItems.limit, props.totalItems.totalItems), locale, 0) }}
                        {{ t("common.labels.of") }} {{  formatNumber(props.totalItems.totalItems, locale, 0) }}
                    </span>
                </v-col>
            </v-col>
            <v-col sm="auto" class="pagination-controls">
                <v-col cols="12" sm="auto" class="pagination-info d-flex align-center gap-2">
                    <span>{{ t('common.labels.goToPage') }}:</span>
                    <v-autocomplete
                        v-model="selectedPage"
                        v-model:search="search"
                        :items="filteredItems"
                        item-title="title"
                        item-value="value"
                        density="compact"
                        hide-details
                        variant="outlined"
                        hide-no-data
                        hide-selected
                        :no-data-text="t('common.messages.info.noData')"
                        @update:modelValue="emitPageChange"
                        autocomplete="off"
                        class="autocomplete"
                    />
                </v-col>
                <div class="pagination-buttons">
                    <button
                        class="pagination-btn d-flex flex-row justify-center align-center"
                        :class="{ disabled: current === 1 }"
                        :disabled="current === 1"
                        @click="onFirst"
                    >
                        <i class="material-symbols-outlined text-lg">first_page</i>
                    </button>
                    <button
                        class="pagination-btn d-flex flex-row justify-center align-center"
                        :class="{ disabled: isPrevDisabled }"
                        :disabled="isPrevDisabled"
                        @click="onPrev"
                    >
                        <i class="material-symbols-outlined text-lg">chevron_left</i>
                    </button>
                    <span
                        :aria-label="t('common.labels.pageOf', {
                        current: currentPage,
                        total: props.totalItems?.totalPages ?? 0
                      })"
                                        >
                      {{ formatNumber(currentPage, locale, 0) }}
                      {{ t('common.labels.of') }}
                      {{ formatNumber(props.totalItems?.totalPages ?? 0, locale, 0) }}
                    </span>

                    <button
                        class="pagination-btn d-flex flex-row justify-center align-center"
                        :class="{ disabled: isNextDisabled }"
                        :disabled="isNextDisabled"
                        @click="onNext"
                    >
                        <i class="material-symbols-outlined text-lg">chevron_right</i>
                    </button>

                    <button
                        class="pagination-btn d-flex flex-row justify-center align-center"
                        :class="{ disabled: isNextDisabled }"
                        :disabled="isNextDisabled"
                        @click="onLast"
                    >
                        <i class="material-symbols-outlined text-lg">last_page</i>
                    </button>
                </div>
            </v-col>
    </v-row>
</template>

<style scoped>

.autocomplete {
    min-width: 100px;
}

.autocomplete * {
    top:1px;
    height: 27px;
    margin-bottom: 1px!important;
    padding-top: 0 !important;
    font-size: 14px;
}

.dark-theme .pagination-container {
    color: white!important;
}
.pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: sans-serif;
    padding: 10px;
    color: #555;
}

tr:hover {
    background-color: rgba(221, 221, 221, 0.25);
}

.pagination-info,
.pagination-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}
.pagination-controls {
    flex-wrap:wrap;
}
.pagination-controls span {
    font-size: 14px;
}

.pagination-buttons {
    display: flex;
    gap: 8px;
}

.pagination-btn:hover {
    background-color: #e9e9e9;
    border-color: #ccc;
}

.pagination-btn.disabled {
    color: #ccc;
    cursor: not-allowed;
    background-color: #f7f7f7;
    border-color: #e0e0e0;
}
</style>
